import { useState } from "react";
import { portfolioImg } from "@/lib/api";
import { PortfolioLightbox } from "@/components/supply/PortfolioLightbox";
import { CATEGORY_LABEL, EXAMPLE_NOTE, PORTFOLIO_CATEGORIES, usePortfolio } from "@/components/supply/portfolio";

const INITIAL = 6;

export const PhotoTile = ({ p, onOpen, testId }) => (
  <button type="button" onClick={onOpen} data-testid={testId}
          className="group block w-full aspect-[4/5] overflow-hidden bg-neutral-100 text-left">
    <img src={portfolioImg(p.id)} alt={p.caption || CATEGORY_LABEL[p.category]} loading="lazy" width={p.width || undefined} height={p.height || undefined}
         onError={(e) => { e.currentTarget.closest("button").style.display = "none"; }}
         className="w-full h-full object-contain block transition-transform duration-500 group-hover:scale-[1.03]" />
  </button>
);

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
          className="font-label text-[0.65rem] px-4 min-h-[44px] border transition-colors"
          style={{ borderColor: active ? "var(--ss-rust)" : "var(--ss-border)", color: active ? "var(--ss-rust)" : "inherit" }}>
    {children}
  </button>
);

export const PortfolioGallery = () => {
  const all = usePortfolio();
  const [cat, setCat] = useState("all");
  const [expanded, setExpanded] = useState(false);
  const [index, setIndex] = useState(null);
  if (!all.length) return null;
  const items = cat === "all" ? all : all.filter((p) => p.category === cat);
  const shown = expanded ? items : items.slice(0, INITIAL);
  const cats = PORTFOLIO_CATEGORIES.filter(([k]) => all.some((p) => p.category === k));
  const pick = (k) => { setCat(k); setExpanded(false); };
  return (
    <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-20 md:pb-28" data-testid="portfolio-gallery">
      <h2 className="font-display text-4xl md:text-5xl">Made by The Social Supply</h2>
      <p className="mt-4 max-w-2xl text-base" style={{ color: "var(--ss-cream-dim)" }} data-testid="portfolio-intro">
        Take a look at some of our custom creations. Every order is designed specifically for your celebration, so colors, wording and details can be customized to fit your event.
      </p>
      {cats.length > 1 && (
        <div className="mt-6 flex md:flex-wrap gap-2 overflow-x-auto no-scrollbar -mx-5 px-5 md:mx-0 md:px-0 [&>button]:shrink-0" data-testid="portfolio-filters">
          <Chip active={cat === "all"} onClick={() => pick("all")} testId="portfolio-filter-all">All</Chip>
          {cats.map(([k, l]) => <Chip key={k} active={cat === k} onClick={() => pick(k)} testId={`portfolio-filter-${k}`}>{l}</Chip>)}
        </div>
      )}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4" data-testid="portfolio-grid">
        {shown.map((p, i) => <PhotoTile key={p.id} p={p} onOpen={() => setIndex(i)} testId={`portfolio-photo-${p.id}`} />)}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="text-xs" style={{ color: "var(--ss-muted)" }} data-testid="portfolio-note">{EXAMPLE_NOTE}</div>
        {items.length > INITIAL && (
          <button type="button" className="btn-outline" onClick={() => setExpanded(!expanded)} data-testid="portfolio-view-more">
            {expanded ? "Show Less" : `View More (${items.length - INITIAL})`}
          </button>
        )}
      </div>
      <PortfolioLightbox items={shown} index={index} setIndex={setIndex} />
    </section>
  );
};
